import {Injectable, OnDestroy} from '@angular/core';

interface TabMessage {
	type: 'heartbeat' | 'register' | 'remove';
	tabId: string;
	timestamp: number;
}

interface TabInfo {
	id: string;
	timestamp: number;
}

@Injectable({
	providedIn: 'root'
})
export class TabCountService implements OnDestroy {
	channel: BroadcastChannel;
	cachedTabs: TabInfo[] = [];
	heartbeatInterval?: ReturnType<typeof setInterval>;

	readonly storageKey = 'dm_open_tabs';
	readonly tabId = Math.random().toString(36).substr(2, 9);

	constructor() {
		this.channel = new BroadcastChannel('dm_tab_management');
		this.channel.onmessage = this.handleMessage.bind(this);

		this.initializeTab();
		this.startHeartbeat();
		this.cleanupDeadTabs();
	}

	private initializeTab(): void {
		this.loadTabsFromStorage();
		this.registerTab();
		this.broadcastMessage({ type: 'register', tabId: this.tabId, timestamp: Date.now() });
	}

	private startHeartbeat(): void {
		this.heartbeatInterval = setInterval(() => {
			this.broadcastMessage({
				type: 'heartbeat',
				tabId: this.tabId,
				timestamp: Date.now()
			});
		}, 2000);
	}

	private registerTab(): void {
		const newTab: TabInfo = {
			id: this.tabId,
			timestamp: Date.now()
		};
		this.cachedTabs.push(newTab);
		this.saveTabsToStorage();
	}

	private loadTabsFromStorage(): void {
		const tabsJson = localStorage.getItem(this.storageKey);
		this.cachedTabs = tabsJson ? JSON.parse(tabsJson) : [];
	}

	private handleTabRegistration(tabId: string, timestamp: number): void {
		if (!this.cachedTabs.some(tab => tab.id === tabId)) {
			this.cachedTabs.push({ id: tabId, timestamp });
			this.saveTabsToStorage();
		}
	}

	private handleMessage(event: MessageEvent<TabMessage>): void {
		switch (event.data.type) {
			case 'heartbeat':
				this.updateTabTimestamp(event.data.tabId, event.data.timestamp);
				break;
			case 'register':
				this.handleTabRegistration(event.data.tabId, event.data.timestamp);
				break;
			case 'remove':
				this.handleTabRemoval(event.data.tabId);
				break;
		}
	}

	private broadcastMessage(message: TabMessage): void {
		this.channel.postMessage(message);
		this.handleMessage(new MessageEvent('message', { data: message }));
	}

	private saveTabsToStorage(): void {
		localStorage.setItem(this.storageKey, JSON.stringify(this.cachedTabs));
	}

	private updateTabTimestamp(tabId: string, timestamp: number): void {
		this.cachedTabs = this.cachedTabs.map(tab =>
			tab.id === tabId ? { ...tab, timestamp } : tab
		);
		this.saveTabsToStorage();
	}

	private handleTabRemoval(tabId: string): void {
		this.cachedTabs = this.cachedTabs.filter(tab => tab.id !== tabId);
		this.saveTabsToStorage();
	}

	private cleanupDeadTabs(): void {
		const now = Date.now();
		const threshold = 9000;

		this.cachedTabs = this.cachedTabs.filter(tab => now - tab.timestamp < threshold);
		this.saveTabsToStorage();
	}

	public handleBeforeUnload(): void {
		this.cachedTabs = this.cachedTabs.filter(tab => tab.id !== this.tabId);
		this.saveTabsToStorage();
		this.broadcastMessage({
			type: 'remove',
			tabId: this.tabId,
			timestamp: Date.now()
		});
	}

	public isLastTab(): boolean {
		return this.cachedTabs.length < 2;
	}

	ngOnDestroy(): void {
		if (this.heartbeatInterval) {
			clearInterval(this.heartbeatInterval);
		}
		window.removeEventListener('beforeunload', this.handleBeforeUnload.bind(this));
		this.channel.close();
		this.handleBeforeUnload();
	}
}
